/**
 * Axios HTTP 客户端 - 与 admin-web 同一套 JWT 自动刷新模式.
 *
 * 设计:
 *   - token 存 localStorage (jinnang_user_ 前缀), store 不持有, 避免循环依赖.
 *   - 请求拦截器注入 `Authorization: Bearer <access_token>`.
 *   - 响应拦截器: 收到 401 -> 暂停后续请求 -> 调 /auth/refresh -> 用新 token 重放;
 *     refresh 也失败 -> 清 token + 回 /login.
 *   - 用独立的 refreshHttp 实例 (无 401 拦截器) 防止递归刷新.
 *   - redirectToLogin 用整页跳转, 让 router 守卫重新接管, 无需在此注入 router.
 *
 * localStorage 键前缀: jinnang_user_  (admin-web 用 jinnang_admin_).
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { TokenResponse } from '@/types/api'
import { showNotify } from '@/stores/notify'

/**
 * API 基地址.
 * - Web: '/api/v1' (由 nginx 反代到后端)
 * - Capacitor 原生 App: 由 .env.production 的 VITE_API_BASE_URL 注入绝对地址
 */
export const apiBaseUrl: string = import.meta.env.VITE_API_BASE_URL || '/api/v1'

/**
 * WebSocket 基地址, 从 apiBaseUrl 推导.
 * - 绝对 http(s) 地址 -> ws(s)://host[:port] (去掉 /api/v1 后缀)
 * - 相对路径 (Web) -> 回退到 window.location.host
 */
export const wsBaseUrl: string = (() => {
  if (/^https?:\/\//.test(apiBaseUrl)) {
    return apiBaseUrl.replace(/^http/, 'ws').replace(/\/api\/v1$/, '')
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}`
})()

const ACCESS_TOKEN_KEY = 'jinnang_user_access_token'
const REFRESH_TOKEN_KEY = 'jinnang_user_refresh_token'

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, access)
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export function redirectToLogin(): void {
  if (window.location.hash !== '#/login') {
    window.location.hash = '/login'
  }
}

export const http: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

// 请求拦截器: 注入 Bearer token.
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ===== 401 自动刷新: pending 队列模式 =====

type PendingItem = {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

let isRefreshing = false
let pendingQueue: PendingItem[] = []

function subscribePending(item: PendingItem): void {
  pendingQueue.push(item)
}

function flushPending(token: string): void {
  pendingQueue.forEach((item) => item.resolve(token))
  pendingQueue = []
}

function rejectPending(error: unknown): void {
  pendingQueue.forEach((item) => item.reject(error))
  pendingQueue = []
}

// 独立 axios 实例, 不挂 401 拦截器, 仅 refresh 使用, 防递归.
const refreshHttp: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

async function doRefresh(): Promise<string> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('无 refresh_token, 请重新登录')
  }
  const res = await refreshHttp.post<TokenResponse>('/auth/refresh', {
    refresh_token: refreshToken,
  })
  setTokens(res.data.access_token, res.data.refresh_token)
  return res.data.access_token
}

/**
 * 刷新 access_token (供 axios 拦截器之外的 fetch 调用复用, 如 SSE 流式).
 * 与 axios 401 拦截器共享同一 isRefreshing/pendingQueue, 避免并发重复刷新.
 * 刷新失败 -> 清 token + 回登录页.
 */
export async function refreshAccessToken(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      subscribePending({ resolve, reject })
    })
  }
  isRefreshing = true
  try {
    const newToken = await doRefresh()
    flushPending(newToken)
    return newToken
  } catch (e) {
    rejectPending(e)
    clearTokens()
    redirectToLogin()
    throw e
  } finally {
    isRefreshing = false
  }
}

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false
  return url.includes('/auth/login') || url.includes('/auth/refresh')
}

let sessionConflictHandled = false

function handleSessionConflict(): void {
  if (sessionConflictHandled) return
  sessionConflictHandled = true
  clearTokens()
  showNotify({
    title: '下线提醒',
    message: '您的账号已在其他设备登录, 您已被迫下线。如非本人操作, 请及时修改密码。',
    confirmText: '重新登录',
    variant: 'danger',
    onConfirm: () => {
      redirectToLogin()
      setTimeout(() => { sessionConflictHandled = false }, 2000)
    },
  })
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined
    const status = error.response?.status
    const errCode = (error.response?.data as { code?: string } | undefined)?.code

    // 账号在其他设备登录 -> 直接弹窗提醒, 不尝试 refresh
    if (errCode === 'SESSION_CONFLICT') {
      handleSessionConflict()
      return Promise.reject(error)
    }

    if (status === 401 && original && !original._retry && !isAuthEndpoint(original.url)) {
      // 并发刷新中: 把请求挂到队列, 等 refresh 成功后用新 token 重放.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribePending({
            resolve: (token: string) => {
              original.headers.Authorization = `Bearer ${token}`
              resolve(http(original))
            },
            reject,
          })
        })
      }
      original._retry = true
      isRefreshing = true
      try {
        const newToken = await doRefresh()
        flushPending(newToken)
        original.headers.Authorization = `Bearer ${newToken}`
        return http(original)
      } catch (refreshError) {
        const refreshErrCode = (axios.isAxiosError(refreshError)
          ? (refreshError.response?.data as { code?: string } | undefined)?.code
          : undefined)
        if (refreshErrCode === 'SESSION_CONFLICT') {
          handleSessionConflict()
        } else {
          rejectPending(refreshError)
          clearTokens()
          redirectToLogin()
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // refresh 端点本身 401: refresh_token 已死, 直接登出.
    if (status === 401 && original?.url?.includes('/auth/refresh')) {
      clearTokens()
      redirectToLogin()
    }

    return Promise.reject(error)
  },
)

/** 从后端 AppException 响应体里抠出 message. */
export function extractErrorMessage(err: unknown, fallback = '请求失败'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined
    if (data?.message) return data.message
    if (err.message) return err.message
  }
  if (err instanceof Error) return err.message
  return fallback
}

/** 透传的 post 包装, 保留与 admin-web 对齐的 API 表面. */
export function rawPost<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
  return http.post<T>(url, data, config)
}
